
import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as echarts from 'echarts';
import { useTheme } from '@/contexts/ThemeContext';
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface ExpenseByCountryChartProps {
  data?: any[];
}

export const ExpenseByCountryChart = ({ data: propData }: ExpenseByCountryChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Date range filter states
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);

  // Filter the data based on date range
  const filteredData = useMemo(() => {
    const sourceData = propData || fetchedData;
    
    if (!startDate && !endDate) {
      return sourceData;
    }
    
    return sourceData.filter(item => {
      if (!item.posting_date) return false;
      
      const postingDate = new Date(item.posting_date);
      const startCheck = !startDate || postingDate >= startDate;
      const endCheck = !endDate || postingDate <= endDate;
      
      return startCheck && endCheck;
    });
  }, [propData, fetchedData, startDate, endDate]);

  // Fetch data from database only when no prop data is provided
  useEffect(() => {
    const fetchData = async () => {
      if (propData) return; // Don't fetch if data is provided via props
      
      setIsLoading(true);
      try {
        const { data: result, error } = await supabase
          .from('kol_tracker')
          .select('expense, language, posting_date')
          .not('expense', 'is', null)
          .not('language', 'is', null);
        
        if (error) {
          console.error('Error fetching expense data:', error);
          return;
        }

        setFetchedData(result || []);
      } catch (error) {
        console.error('Error fetching expense data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [propData]); // Removed startDate and endDate since we're filtering client-side now

  // Clear filters function
  const clearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  // Check if filters are active
  const hasActiveFilters = startDate || endDate;
  
  useEffect(() => {
    let chart: echarts.ECharts | undefined;

    // Process data to organize by language
    const processData = () => {
      if (!filteredData || filteredData.length === 0) {
        return {
          sourceData: [['Language', 'Region', 'Expense']],
          regionExpenses: {}
        };
      }

      // Extract unique languages and calculate expenses
      const languageExpenses: Record<string, number> = {};
      
      // Calculate total expense and collect languages
      filteredData.forEach(item => {
        if (!item.expense || !item.language) return;
        
        const language = item.language;
        languageExpenses[language] = (languageExpenses[language] || 0) + item.expense;
      });
      
      // Define region mapping for grouping
      const regionMapping: Record<string, string> = {
        'Chinese': 'Asia',
        'Japanese': 'Asia',
        'Korean': 'Asia',
        'English': 'Global',
        'Spanish': 'Europe',
        'Portuguese': 'Europe',
        'French': 'Europe',
        'German': 'Europe',
        'Italian': 'Europe',
        'Russian': 'Europe',
        // Add more mappings as needed
      };
      
      // Pre-aggregate data manually instead of using transform
      const regionExpenses: Record<string, number> = {};
      Object.entries(languageExpenses).forEach(([language, expense]) => {
        const region = regionMapping[language] || 'Other';
        regionExpenses[region] = (regionExpenses[region] || 0) + expense;
      });
      
      // Create source data for dataset
      const sourceData: any[] = [['Language', 'Region', 'Expense']];
      
      // Fill source data
      Object.entries(languageExpenses).forEach(([language, expense]) => {
        const region = regionMapping[language] || 'Other';
        sourceData.push([language, region, expense]);
      });
      
      return {
        sourceData,
        regionExpenses
      };
    };
    
    if (chartRef.current) {
      // Dispose existing chart before creating new one
      if (chart) {
        chart.dispose();
      }
      
      chart = echarts.init(chartRef.current);
      
      // Updated: Reverting back to original color palette (blue/teal/green tones instead of purple)
      const colorPalette = [
        '#3b82f6', '#0EA5E9', '#06b6d4', '#4ade80',
        '#f97316', '#f59e0b', '#14b8a6', '#6366f1',
        '#ec4899', '#8b5cf6', '#f43f5e', '#10b981'
      ];
      
      const { sourceData, regionExpenses } = processData();
      
      // Create region data for outer ring
      const regionData = Object.entries(regionExpenses).map(([name, value]) => ({ name, value }));
      
      // Create language data for inner ring, grouped by region
      const languageData: any[] = [];
      sourceData.slice(1).forEach(item => {
        const language = item[0];
        const region = item[1];
        const expense = item[2];
        
        languageData.push({
          name: language,
          value: expense,
          itemStyle: {
            borderRadius: 5,
            borderWidth: 0, // Removed white border - was 2
            borderColor: 'transparent' // Made border transparent - was isLightMode ? '#fff' : '#333'
          },
          // Adding parent info for tooltip
          region: region
        });
      });

      // Show "No Data" message if no data is available
      if (regionData.length === 0) {
        const option: echarts.EChartsOption = {
          title: {
            text: 'No Data Available',
            subtext: hasActiveFilters ? 'Try adjusting your date filters' : 'No expense data found',
            left: 'center',
            top: 'center',
            textStyle: {
              color: isLightMode ? '#333' : '#fff',
              fontSize: 24
            },
            subtextStyle: {
              color: isLightMode ? '#666' : '#ccc',
              fontSize: 16
            }
          }
        };
        chart.setOption(option);
      } else {
        const option: echarts.EChartsOption = {
          legend: {
            type: 'scroll',
            orient: 'vertical',
            right: 10,
            top: 'center',
            textStyle: {
              color: isLightMode ? '#333' : '#ddd'
            }
          },
          tooltip: {
            trigger: 'item',
            formatter: (params: any) => {
              if (params.componentSubType === 'pie') {
                const value = params.value;
                const formattedValue = typeof value === 'number' ? value.toLocaleString() : value;
                
                if (params.seriesName === 'Region') {
                  return `${params.name}<br/>Expense: $${formattedValue} (${params.percent}%)`;
                } else {
                  const regionName = params.data.region || 'Unknown';
                  return `${params.name} (${regionName})<br/>Expense: $${formattedValue} (${params.percent}%)`;
                }
              }
              return '';
            },
            backgroundColor: isLightMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(50, 50, 50, 0.9)',
            borderColor: isLightMode ? '#ddd' : '#555',
            textStyle: {
              color: isLightMode ? '#333' : '#fff'
            }
          },
          title: {
            text: 'Expense Distribution by Language',
            left: 'center',
            textStyle: {
              color: isLightMode ? '#333' : '#fff'
            }
          },
          series: [
            {
              name: 'Region',
              type: 'pie',
              radius: ['15%', '40%'],
              center: ['50%', '50%'],
              data: regionData,
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              },
              itemStyle: {
                borderRadius: 7,
                borderWidth: 0, // Removed white border - was 2
                borderColor: 'transparent' // Made border transparent - was isLightMode ? '#fff' : '#333'
              },
              label: {
                show: false
              },
            },
            {
              name: 'Language',
              type: 'pie',
              radius: ['45%', '75%'],
              center: ['50%', '50%'],
              data: languageData,
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              }
            }
          ],
          color: colorPalette
        };

        chart.setOption(option, true); // Use true to replace completely
      }
      
      // Handle resize
      const handleResize = () => {
        chart?.resize();
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        chart?.dispose();
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [filteredData, theme, isLightMode, hasActiveFilters]); // Use filteredData instead of data

  return (
    <div className="space-y-4">
      {/* Date Range Filter Controls */}
      <div className={`p-4 rounded-lg border ${isLightMode ? 'bg-gray-50 border-gray-200' : 'bg-gray-800 border-gray-700'}`}>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className={`h-4 w-4 ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <span className={`text-sm font-medium ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
              Filter by Posting Date:
            </span>
          </div>

          {/* Start Date Picker */}
          <Popover open={isStartDateOpen} onOpenChange={setIsStartDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[180px] justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "PPP") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  setStartDate(date);
                  setIsStartDateOpen(false);
                }}
                disabled={(date) => date > new Date()}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>

          {/* End Date Picker */}
          <Popover open={isEndDateOpen} onOpenChange={setIsEndDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[180px] justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "PPP") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => {
                  setEndDate(date);
                  setIsEndDateOpen(false);
                }}
                disabled={(date) => {
                  const today = new Date();
                  const minDate = startDate || new Date('2020-01-01');
                  return date > today || date < minDate;
                }}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="mr-1 h-4 w-4" />
              Clear
            </Button>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <span className={`text-sm ${isLightMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Loading...
            </span>
          )}
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="mt-2 flex flex-wrap gap-2">
            {startDate && (
              <span className={`text-xs px-2 py-1 rounded ${isLightMode ? 'bg-blue-100 text-blue-800' : 'bg-blue-900 text-blue-200'}`}>
                From: {format(startDate, "MMM dd, yyyy")}
              </span>
            )}
            {endDate && (
              <span className={`text-xs px-2 py-1 rounded ${isLightMode ? 'bg-blue-100 text-blue-800' : 'bg-blue-900 text-blue-200'}`}>
                To: {format(endDate, "MMM dd, yyyy")}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Chart Container */}
      <div ref={chartRef} style={{ width: '100%', height: '800px' }} />
    </div>
  );
};
